import { t } from '@/features/i18n/utils';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderUserMenu } from '@/ui/components/header';
import { MainContainer, MainScrollArea } from '@/ui/components/main';
import { sharepointConnectionStatusQuery } from '@/features/sharepoint/queries';
import { useSharepointLogin } from '@/features/sharepoint/helpers';
import { useSignOutSharepoint } from '@/features/sharepoint/mutations';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/ui/components/alert-dialog';
import { Button } from '@/ui/components/button';
import { Card } from '@/ui/components/card';
import { Spinner } from '@/ui/components/spinner';
import { Icons } from '@/ui/components/icons';
import { Icon } from '@/ui/components/icon';
import { LogoutSquare02Icon } from '@hugeicons-pro/core-stroke-rounded';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';

export const Route = createFileRoute('/_authed/settings/sharepoint')({
  component: RouteComponent,
});

function RouteComponent() {
  const login = useSharepointLogin();
  const signOut = useSignOutSharepoint();

  const { data, isLoading } = useQuery(sharepointConnectionStatusQuery());

  if (isLoading) return <Spinner kind="inset" />;

  if (!data?.connected)
    return (
      <Empty>
        <EmptyMedia className="[&_svg]:size-16">
          <Icons.sharepoint />
        </EmptyMedia>
        <EmptyContent className="max-w-xl">
          <EmptyTitle>
            {t({ en: 'IT admin approval required', nb: 'Godkjenning fra IT-admin kreves' })}
          </EmptyTitle>
          <EmptyDescription>
            {t({
              en: 'Your IT admin needs to approve Volve AI as a Microsoft 365 / SharePoint integration before you can sign in and import files. If this is already approved, sign in below.',
              nb: 'IT-admin må godkjenne Volve AI som en Microsoft 365 / SharePoint-integrasjon før du kan logge inn og importere filer. Hvis dette allerede er godkjent, logg inn under.',
            })}
          </EmptyDescription>
          <Button kind="with-icon" variant="secondary" onClick={login}>
            <Icons.sharepoint />
            {t({ en: 'Sign in to SharePoint', nb: 'Logg inn på SharePoint' })}
          </Button>
        </EmptyContent>
      </Empty>
    );

  return (
    <>
      <Header>
        <HeaderBackButton to="/" />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icons.sharepoint />
            {t({ en: 'SharePoint', nb: 'SharePoint' })}
          </BreadcrumbPage>
        </Breadcrumb>
        <HeaderUserMenu />
      </Header>
      <MainScrollArea>
        <MainContainer className="max-w-xl">
          <Card className="p-4">
            <div className="grid grid-cols-[auto_1fr] gap-4">
              <div>
                <div>
                  <h3 className="text-lg font-semibold py-0.75">{data.displayName}</h3>
                  <p className="text-sm text-muted-foreground">
                    {data.email &&
                      t({ en: `Signed in as ${data.email}`, nb: `Logget inn som ${data.email}` })}
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger
                    render={
                      <Button kind="with-icon" size={'lg'} variant="secondary">
                        <Icon icon={LogoutSquare02Icon} />
                        {t({ en: 'Sign out', nb: 'Logg ut' })}
                      </Button>
                    }
                  />
                  <AlertDialogPopup>
                    <AlertDialogTitle>{t({ en: 'Sign Out', nb: 'Logg ut' })}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t({
                        en: 'Files imported from SharePoint will not be deleted',
                        nb: 'Filer importert fra SharePoint blir ikke slettet',
                      })}
                    </AlertDialogDescription>
                    <AlertDialogFooter>
                      <AlertDialogCancel />
                      <Button pending={signOut.isPending} onClick={() => signOut.mutate()}>
                        {t({ en: 'Sign out', nb: 'Logg ut' })}
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogPopup>
                </AlertDialog>
              </div>
            </div>
          </Card>
        </MainContainer>
      </MainScrollArea>
    </>
  );
}
